import { Dialog, DialogContent, DialogTrigger, IconButton, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import type { ProjectDetailsParams } from '../..';
import store from '../store';
import UtilityContacts from '../UtilityContacts';
import { useUtilitiesPageRedesignEnabled } from '../useUtilitiesPageRedesignEnabled';
import { useReportExports } from './useReportExports';

export function MapChildren() {
  return null;
}

export function TabDetails() {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { isLoading, isError, project, isFullUtilitiesExperienceEnabled, isUtilitiesPageEnabled } = useSnapshot(
    store,
  ) as typeof store;

  const isPageRedesignEnabled = useUtilitiesPageRedesignEnabled();
  // Contacts moves to the Utilities page button, so Reports keeps it for anyone who can't open that page.
  const showContacts = !isPageRedesignEnabled || !isUtilitiesPageEnabled;

  const { hasActiveRequest, exportOutreachLog, openRecordsResearchReport, openCommunicationLog, modals } =
    useReportExports(projectId, 'reports_tab');
  const isDisabled = !hasActiveRequest;

  const utilityContactsDialog = (triggerTestId: string) => (
    <Dialog>
      <DialogTrigger asChild>
        <IconButton
          variant="secondary"
          icon="download"
          size="icon"
          aria-label="Download Utility Contacts"
          className="p-1.5"
          data-testid={triggerTestId}
        />
      </DialogTrigger>
      <DialogContent
        title="Utility Contacts"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="utilities-reports-utility-contacts-dialog"
      >
        <UtilityContacts source="reports_tab" />
      </DialogContent>
    </Dialog>
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.fetchData(projectId);
  }, []);

  function reportItem(title: string, description: string, onClick: (() => void) | ReactNode, disabled?: boolean) {
    return (
      <div className="flex items-center justify-between">
        <div className="w-5/6">
          <div className="text-base font-medium text-secondary">{title}</div>
          <div className="text-sm">{description}</div>
        </div>
        <div>
          {typeof onClick === 'function' ? (
            <Can action={Action.ExportOutreachLog}>
              <IconButton
                variant="secondary"
                disabled={disabled ?? isDisabled}
                onClick={onClick}
                icon="download"
                size="icon"
                aria-label={`Download ${title}`}
                className="p-1.5"
                tooltip={`Download ${title} ${(disabled ?? isDisabled) ? 'disabled' : ''}`}
              />
            </Can>
          ) : (
            onClick
          )}
        </div>
      </div>
    );
  }

  if (isLoading || !project) {
    return <SkeletonSpinnerCentered />;
  }

  if (isError) {
    console.error(isError, 'failed loading utilities/reports');
    return <div>Ooops, something is wrong. Please contact support.</div>;
  }

  if (!isFullUtilitiesExperienceEnabled) {
    return (
      <div className="space-y-4">
        {reportItem(
          'Utilities List',
          'Lis of all utilities with maps, agency & asset information in the project area',
          openRecordsResearchReport,
          false,
        )}
        {showContacts &&
          reportItem(
            'Utility Contacts',
            'Agency contacts (emails & phone numbers) list',
            utilityContactsDialog('utilities-reports-utility-contacts-basic'),
          )}
        {modals}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isDisabled && (
        <div className="text-sm">
          <b>Note:</b> Some reports are not available until requests are initiated.
        </div>
      )}

      {reportItem(
        'Records Research Report',
        'Detailed report with maps, agency & asset information, data request status, and activity history',
        openRecordsResearchReport,
        false,
      )}
      {showContacts &&
        reportItem(
          'Utility Contacts',
          'Agency contacts (emails & phone numbers) list',
          utilityContactsDialog('utilities-reports-utility-contacts'),
        )}
      {reportItem(
        'Outreach Log',
        'Table documenting all agency communications with dates and statuses, sorted by agency',
        exportOutreachLog,
        false,
      )}
      {reportItem('Utility Communication', 'Detailed email logs of agency communications', openCommunicationLog)}
      {modals}
    </div>
  );
}
